/*
* Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
*/
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useQueryClient} from 'react-query';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import isEmpty from 'lodash.isempty';
import {globalSearchSelector} from '@ion/applications/src/globalsearch/selector';
import useCurrentGlobalGroupId from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import useFilePermissionsAction from '@ion/applications/src/hooks/useFilePermissionsAction';
import useAuthenticatedUser from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import useElementResize from '@ion/core/src/hooks/useElementResize';
import {translate} from '@ion/core/src/locale/locale';
import {loadJobDevicesSearch} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {
  ApplicationFeatures,
  CRUDPermissions,
  Entity,
  GenericObject
} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import Render from '@ion/dls/src/render/Render';
import {DLSTheme} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import useCancelTask from '@ion/query/src/hooks/useCancelTask';
import useCancelTaskDevice from '@ion/query/src/hooks/useCancelTaskDevice';
import {useGetOtaState} from '@ion/query/src/hooks/useGetOtaState';
import useJobId from '@ion/query/src/hooks/useJobId';
import usePaginatedQueryCache from '@ion/query/src/hooks/usePaginatedQueryCache';
import {
  cancelJobs,
  displayRerunJobSearchEngineDialog,
  NAME,
  setJobIdDevicesFilter
} from '@/store/actions/jobsearchengine';
import {jobSearchEngineTabsSelector} from '@/store/selectors/jobsearchengine';
import {cancelDevices, cancelTasks} from '../../../store/actions/jobsearchengine';
import {PREFERENCE} from '../../../store/reducers/jobsearchengine';
import {
  deviceCancelDeviceSelectedSelector,
  deviceCancelSelectedSelector,
  deviceCancelShowDialogSelector,
  taskCancelDeviceSelectedSelector,
  taskCancelShowDialogSelector
} from '../../../store/selectors/jobsearchengine';
import DeviceCancelPrompt from '../jobs/DeviceCancelPrompt';
import TaskCancelPrompt from '../jobs/TaskCancelPrompt';
import JobDevicesSearchWidget from './JobDevicesSearchWidget';
import JobFacetsDrawer from './JobFacetsDrawer';
import {jobCategories} from './JobFacetUtils';
import {isJobScopedKey, resolveHeight} from './JobSearchEngineUtils';
import {getDeleteMessage} from './JobSearchUtils';
import JobSummary from './JobSummary';
import TaskDevicesGrid, {TaskDevicesGridAPI} from './TaskDevicesGrid';
import TasksGrid from './TasksGrid';
import useJobPagination from './useJobPagination';
import useJobSelectedFacets from './useJobSelectedFacets';
import useJobTabLiveUpdates from './useJobTabLiveUpdates';

const paging = new SmartSearchPaging();
export interface JobTabProps {
  jobId: number;
  isFacetOpen: boolean;
  params: GenericObject;
  dropdownOpen?: boolean;
  setDropdownOpen?: Function;
  activeTab: string;
  jobsPermissions?: CRUDPermissions;
  Features?: ApplicationFeatures;
  containerHeight?: boolean;
  setEnableApi?: Function;
  enableApi?: boolean;
  currentJob: GenericObject;
  setCurrentJob?: Function;
  rerunJob?: Function;
  includeActions?: boolean;
  actions?: ReactNode;
  setIsRerunFailed?: Function;
  setIsJobLoading?: Function;
  setIsTaskDeviceClicked?: Function;
}

export const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    slideUp : {
      animation : '$slideUp 750ms cubic-bezier(0.4, 0.0, 0.2, 1) forwards'
    },
    '@keyframes slideUp' : {
      '0%' : {
        transform : 'translateY(100%)',
        opacity   : 0
      },
      '100%' : {
        transform : 'translateY(0)',
        opacity   : 1
      }
    },
    taskTable : {
      height  : '100%',
      display : 'flex'
    },
    spinner : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      width          : '100%',
      height         : 100
    },
    header : {
      padding  : 12,
      fontSize : '14px !important'
    },
    taskDevicesGrid : {
      height        : '100%',
      display       : 'flex',
      flexDirection : 'column',
      overflow      : 'hidden',
      marginTop     : '0px',
      marginBottom  : '0px',
      position      : 'relative',
      flex          : 1
    },
    actions : {
      display        : 'flex',
      justifyContent : 'space-between'
    }
  };
});

const TASK_GRID_CONTAINER = 'tasksGridContainer';

// stable reference - a fresh [] would make searchedValue look changed on
// every unrelated re-render, re-triggering its effects and resetting the grid...
const EMPTY_DEVICES_FILTER: string[] = [];

const validateSearch = (filter: string[]): string[] => (
  filter?.length ? filter : EMPTY_DEVICES_FILTER
);

/**
* The component that displays the selected job's tasks and devices details.
*/
export const JobTab = ({
  jobId,
  rerunJob,
  enableApi,
  setEnableApi,
  isFacetOpen,
  jobsPermissions,
  params,
  activeTab,
  Features,
  containerHeight,
  dropdownOpen,
  currentJob,
  setCurrentJob,
  setDropdownOpen,
  setIsRerunFailed,
  setIsTaskDeviceClicked,
  setIsJobLoading,
  actions,
  includeActions = false
}: JobTabProps, ref) => {
  const classes                             = useStyles();
  const dispatch                            = useDispatch();
  const queryClient                         = useQueryClient();
  const sortOnRef                           = useRef();
  const devicesGridRef                      = useRef<TaskDevicesGridAPI>(null);
  const tabs                                = useSelector(jobSearchEngineTabsSelector);
  const currentTabFilter                    = tabs.find(tab => tab.key === activeTab)?.devicesFilter;
  const [selectedTask, setSelectedTask]     = useState(0);
  const [searchValue, setSearchValue]       = useState('');
  const [searchedValue, setSearchedValue]   = useState(validateSearch(currentTabFilter));
  const [isExact, setIsExact]               = useState(false);
  const [otaCode, setOtaCode]               = useState('');
  const {
    search : {
      isViewing
    }
  } = useSelector(globalSearchSelector);
  const {account}                           = useAuthenticatedUser();
  const globalGroupaccountId                = useCurrentGlobalGroupId();
  const accountId                           = isViewing ? entityId(account) : globalGroupaccountId;
  const {statusCode}                        = useGetOtaState();
  const [searchChanged, setsearchChanged]   = useState(false);
  const {height: customHeight}              = useElementResize(TASK_GRID_CONTAINER, searchChanged);
  const customHeightRef                     = useRef(customHeight);

  const isTaskEditing      = useSelector(taskCancelShowDialogSelector);
  const taskSelected       = useSelector(taskCancelDeviceSelectedSelector);
  const isDeviceEditing    = useSelector(deviceCancelShowDialogSelector);
  const selected           = useSelector(deviceCancelSelectedSelector);
  const deviceSelected     = useSelector(deviceCancelDeviceSelectedSelector);

  const {
    getPaginationForTab,
    clearPaginationForTab
  } = useJobPagination();

  const {
    selectedJobFacetsSignal,
    getSelectedFacetsForTab
  } = useJobSelectedFacets();

  useEffect(() => {
    if (customHeight > 0) {
      customHeightRef.current = customHeight;
    }
  }, [customHeight]);

  const devicesGridHeight = resolveHeight(customHeight, customHeightRef.current, 94);

  const {
    failureMessage,
    successMessage
  } = translate('jobsearchengine:cancel', {
    returnObjects : true,
    entity        : Entity.task
  }) || {};

  const paramsTab    = params?.activeTab;
  const enableTabApi = enableApi && !paramsTab?.includes('jobs');

  // guard, not a live filter: apiParams carries no pageId/pageSize today. Both
  // reflect the jobs-list pagination rather than this job's own identity, so
  // letting either back in would refetch the job details every time the task
  // devices grid below repaginated for this tab...
  const jobParams = useMemo(() => {
    const {pageId, pageSize, ...jobIdParams} = params ?? {};

    return {...jobIdParams, accountId, includeSubGroups: true};
  }, [params, accountId]);

  const jobKey = useMemo(() => (
    createKey(QueryKey.LOAD_JOB_ID, jobParams)
  ), [jobParams]);

  const {
    job,
    refresh: onJobIdRefresh,
    isFetching,
    isFetched
  } = useJobId(jobParams, enableTabApi) || {};

  // currentJob is JobSearchEngine-level and holds whichever job resolved last,
  // which may belong to another tab - only fall back to it when it is actually
  // this tab's job, otherwise show the empty summary...
  const lastKnownJob = currentJob?.jobId === jobId ? currentJob : job;

  useEffect(() => {
    if (isFetched) {
      setEnableApi?.(false);
    }
  }, [isFetched]);

  useEffect(() => {
    if (!isEmpty(job) && !isFetching) {
      setCurrentJob?.(job);
    }
  }, [job, isFetching]);

  useEffect(() => {
    setIsJobLoading?.(isFetching);
  }, [isFetching]);

  const {
    isError: taskCancelError,
    isSuccess: taskCancelSuccess,
    isLoading: taskCancelLoading,
    cancelTask: cancelSelectedtask
  } = useCancelTask();

  const {
    isError: taskDeviceCancelError,
    isSuccess: taskDeviceCancelSuccess,
    isLoading: taskDeviceCancelLoading,
    cancelTaskDevice: cancelSelectedtaskDevice
  } = useCancelTaskDevice();

  useEffect(() => {
    setSelectedTask(0);
    setSearchedValue(validateSearch(currentTabFilter));
  }, [jobId, currentTabFilter]);

  const resolveResults = useCallback(function({items = [], aggregations = [], totalCount = 0}, key) {
    return {items, aggregations, totalCount};
  }, []);

  const initialPage     = getPaginationForTab(activeTab)?.page ?? 1;
  const initialPageSize = getPaginationForTab(activeTab)?.pageSize ?? 20;

  const {
    data,
    loadItems,
    refresh,
    onRefresh,
    fetching: isLoading,
    paging: tabPaging
  } = usePaginatedQueryCache({
    api             : loadJobDevicesSearch,
    baseKey         : QueryKey.LOAD_TASK_DEVICES,
    parseKey        : 'taskDevices',
    initialPage,
    initialPageSize,
    // prefetch races paging.nextPage()'s state update against the ref
    // usePaginatedQueryCache only updates from loadItems, which can cache a
    // page's data under the wrong query key. Disabled until fixed centrally...
    enabledPrefetch : false,
    parser          : resolveResults
  });

  const invalidateDevices = useCallback(() => {
    queryClient.invalidateQueries({
      predicate : query => isJobScopedKey(query.queryKey, QueryKey.LOAD_TASK_DEVICES, jobId)
    });
  }, [queryClient, jobId]);

  const {onDevicesSettled} = useJobTabLiveUpdates({
    queryClient,
    jobKey,
    jobId,
    isFetching,
    selectedTask,
    devicesGridRef,
    invalidateDevices
  });

  const buildFacetQueryString = useCallback((facets: Record<string, string[]>, existingQuery: string = '') => {
    let query = existingQuery;
    Object.keys(facets).forEach((facetCategory) => {
      const selectedValues = facets[facetCategory];
      if (selectedValues?.length) {
        const cleanedCategory = facetCategory.replace('-terms', '');
        const facetQuery = selectedValues.map((value) => `${cleanedCategory}:${value}`).join(' OR ');
        query += ` AND ${facetQuery}`;
      }
    });
    return query;
  }, []);

  const queryString = useMemo(() => {
    const baseQuery = `jobId: ${jobId} AND taskIndex: ${selectedTask}`;
    return buildFacetQueryString(getSelectedFacetsForTab(activeTab), baseQuery);
  }, [
    selectedJobFacetsSignal.value,
    selectedTask,
    jobId
  ]);

  const queryPayload = useMemo(() => {
    const hasDeviceIdentifiers = Array.isArray(searchedValue) && searchedValue.length > 0;
    const queryObject = hasDeviceIdentifiers
      ? {
        bool : {
          must : [
            {
              terms : {
                deviceIdentifier : searchedValue
              }
            },
            {
              query_string : {
                query : `(${queryString})`
              }
            }
          ]
        }
      }
      : {
        query_string : {
          query : queryString
        }
      };

    return queryObject;
  }, [searchedValue, queryString]);

  useEffect(() => {
    onRefresh();
    setEnableApi?.(true);
    setsearchChanged(prev => !prev);
    dispatch(setJobIdDevicesFilter(searchedValue) as any
    );
  }, [searchedValue]);

  // resets pagination only on user-driven search changes, not tab switches...
  const onSearchedValueChange = useCallback((update) => {
    clearPaginationForTab(activeTab);
    setSearchedValue(update);
  }, [activeTab, clearPaginationForTab]);

  useEffect(() => {
    if (taskCancelSuccess || taskCancelError) {
      dispatch(cancelTasks(null, false));
    }
  }, [taskCancelSuccess, taskCancelError]);

  useEffect(() => {
    if (taskDeviceCancelSuccess || taskDeviceCancelError) {
      dispatch(cancelDevices(null, null, false));
    }
  }, [taskDeviceCancelSuccess, taskDeviceCancelError]);

  // page is ag-grid's own 0-indexed counter and must not reach the query key or
  // the API - pageId is the only page address this grid addresses by...
  const getDevices = useCallback(({callback, page, pageId, sort, ...params}) => {
    sortOnRef.current = sort;
    return loadItems({
      ...params,
      paging,
      hasPaging        : true,
      callback,
      accountId,
      jobId,
      selectedTask,
      pageId,
      includeSubGroups : true,
      query            : queryPayload,
      sortOn           : sort
    });
  }, [
    selectedTask,
    loadItems,
    jobId
  ]);

  const refreshJob = useCallback(() => {
    onRefresh();
    setEnableApi?.(true);
    onJobIdRefresh();
    setDropdownOpen(false);
    setsearchChanged(prev => !prev);
  }, []);

  useImperativeHandle(ref, () => ({
    refreshDevices : onRefresh,
    refresh        : refreshJob,
    searchCriteria : queryString?.replace(/AND taskIndex: \s*\d/g, ''),
    sortOn         : sortOnRef?.current,
    job
  }), [refresh, job, queryString]);

  useEffect(() => {
    if (
      (jobCategories.includes('otaStatCode-terms')) ||
      (jobCategories.includes('otaDiagnosticCode-terms'))
    ) {
      setOtaCode(JSON.stringify(statusCode));
    }
  }, [jobCategories, statusCode]);

  const onTaskDeleteConfirm = useCallback(function() {
    cancelSelectedtask(taskSelected, selectedTask);
    onJobIdRefresh();
  }, [taskSelected, selectedTask]);

  const onTaskDeleteCancel = useCallback(function() {
    dispatch(cancelTasks(taskSelected, false));
  }, [taskSelected]);

  const getTaskDeleteMessage = useCallback(function() {
    return getDeleteMessage(taskSelected, selectedTask);
  }, [taskSelected, selectedTask]);

  const onDeviceDeleteConfirm = useCallback(function() {
    cancelSelectedtaskDevice(selected, deviceSelected, selectedTask);
  }, [deviceSelected, selected, selectedTask]);

  const onDeviceDeleteCancel = useCallback(function() {
    dispatch(cancelDevices(deviceSelected, selected, false));
  }, [deviceSelected, selected]);

  const getDeviceDeleteMessage = useCallback(function() {
    return getDeleteMessage(deviceSelected);
  }, [deviceSelected]);

  const hasAssignFilePermission = useFilePermissionsAction({
    Features,
    isEditor : true
  });

  return (
    <>
      <Render.If condition={isFetching}>
        <div className={classes.spinner}>
          <IonSpinner width='25px' height='25px' />
        </div>
      </Render.If>
      <Render.If condition={!isFetching && isFetched}>
        <JobFacetsDrawer aggregations={data?.current?.totalCount ? data?.current?.aggregations : []}
          activeTab={activeTab}
          isFacetOpen={isFacetOpen}
          facetCategories={jobCategories}
          isLoading={isLoading}
          otaCode={otaCode}
          onRefresh={onRefresh}
          hideDateFacets />
        <div className={classes.actions}>
          <JobDevicesSearchWidget isExact={isExact}
            activeTab={activeTab}
            setIsExact={setIsExact}
            searchValue={searchValue}
            preference={PREFERENCE}
            dropdownOpen={dropdownOpen}
            searchedValue={searchedValue}
            setSearchValue={setSearchValue}
            setDropdownOpen={setDropdownOpen}
            setSearchedValue={onSearchedValueChange} />
          <Render.If condition={includeActions}>
            {actions}
          </Render.If>
        </div>
        <JobSummary job={isEmpty(job) ? lastKnownJob : job} />
        <div id={TASK_GRID_CONTAINER}>
          <TasksGrid job={job}
            setIsRerunFailed={setIsRerunFailed}
            selectedTask={selectedTask}
            Features={Features}
            jobsPermissions={jobsPermissions}
            rerunJob={rerunJob}
            setSelectedTask={setSelectedTask}
            hasAssignFilePermission={hasAssignFilePermission} />
        </div>
        <HeaderTypography variant={'h4'} className={classes.header}>
          {`${translate('jobsearchengine:taskDetails.task')} ${selectedTask + 1}`}
        </HeaderTypography>
        <div className={classnames(classes.slideUp, classes.taskTable)}>
          <div className={classes.taskDevicesGrid}>
            <TaskDevicesGrid jobId={jobId}
              ref={devicesGridRef}
              activeTab={activeTab}
              tabPaging={tabPaging}
              setEnableApi={setEnableApi}
              customHeight={devicesGridHeight}
              containerHeight={containerHeight}
              setIsTaskDeviceClicked={setIsTaskDeviceClicked}
              taskIndex={selectedTask}
              jobs={job}
              Features={Features}
              jobsPermissions={jobsPermissions}
              refresh={refresh || taskDeviceCancelLoading}
              onRefresh={onRefresh}
              onSettled={onDevicesSettled}
              getDevices={getDevices} />
          </div>
        </div>
      </Render.If>
      <TaskCancelPrompt show={isTaskEditing}
        isCancelTask={true}
        onDeleteConfirm={onTaskDeleteConfirm}
        onDeleteCancel={onTaskDeleteCancel}
        overrideMessage={getTaskDeleteMessage}
        downloadjob={taskSelected}
        index={selectedTask}
        result={taskCancelSuccess && successMessage}
        loading={taskCancelLoading}
        error={taskCancelError && failureMessage} />
      <DeviceCancelPrompt show={isDeviceEditing}
        isCancelDevice={true}
        isJobSearchEngine={true}
        onDeleteConfirm={onDeviceDeleteConfirm}
        onDeleteCancel={onDeviceDeleteCancel}
        overrideMessage={getDeviceDeleteMessage}
        downloadjob={deviceSelected}
        result={taskDeviceCancelSuccess && successMessage}
        loading={taskDeviceCancelLoading}
        error={taskDeviceCancelError && failureMessage} />
    </>
  );
};

export default forwardRef(JobTab);
